import { ScrollShadow } from '@heroui/react';
import clsx from 'clsx';
import React, { forwardRef, useMemo } from 'react';
import type { ListboxContextValue, ListboxProps } from './types';
import { ListboxContext } from './context';

// 自定义 Listbox 组件
export const Listbox = forwardRef<HTMLUListElement, ListboxProps>((props, ref) => {
    const {
        children,
        as: Component = 'div',
        selectionMode = 'none',
        selectedKeys,
        disabledKeys,
        onSelectionChange,
        onAction,
        onKeyDown,
        hideSelectedIcon = false,
        disallowEmptySelection = false,
        variant = 'flat',
        color = 'default',
        topContent,
        bottomContent,
        emptyContent = "No items.",
        className,
        classNames,
        itemClasses,
        ...restProps
    } = props;
    const contextValue = useMemo<ListboxContextValue>(() => ({
        selectionMode,
        selectedKeys,
        disabledKeys,
        hideSelectedIcon,
        disallowEmptySelection,
        variant,
        color,
        itemClasses,
        onSelectionChange,
        onAction,
    }), [selectionMode, selectedKeys, disabledKeys, hideSelectedIcon, disallowEmptySelection, variant, color, itemClasses, onSelectionChange, onAction]);

    const baseClasses = useMemo(() => clsx(
        "flex flex-col gap-0",
        classNames?.base,
        className
    ), [classNames?.base, className]);

    const listClasses = useMemo(() => clsx(
        "flex flex-col gap-0 outline-none",
        classNames?.list
    ), [classNames?.list]);

    const emptyContentClasses = useMemo(() => clsx(
        "flex items-center justify-center px-6 py-8 text-sm text-default-400",
        classNames?.emptyContent
    ), [classNames?.emptyContent]);

    const hasChildren = React.Children.count(children) > 0;

    return (
        <ListboxContext.Provider value={contextValue}>
            <Component className={baseClasses} {...restProps}>
                {topContent && (
                    <div className="flex-shrink-0">
                        {topContent}
                    </div>
                )}

                <ScrollShadow
                    hideScrollBar
                    className="flex-1"
                    orientation="vertical"
                >
                    {hasChildren ? (
                        <ul
                            ref={ref}
                            className={listClasses}
                            role="listbox"
                            aria-multiselectable={selectionMode === 'multiple'}
                            onKeyDown={onKeyDown}
                        >
                            {children}
                        </ul>
                    ) : (
                        <div className={emptyContentClasses}>
                            {emptyContent}
                        </div>
                    )}
                </ScrollShadow>

                {bottomContent && (
                    <div className="flex-shrink-0">
                        {bottomContent}
                    </div>
                )}
            </Component>
        </ListboxContext.Provider>
    );
});

Listbox.displayName = "Listbox"; 